/**
 * Turning a session's events into the list of clips to cut.
 *
 * This is the one place that decides what a recording's clips are: which
 * moments, in what order, with what boundaries in recording time, carrying
 * which game and which audience score. Everything downstream — the worker
 * writing rows, Studio showing a review list before it renders, the dashboard
 * asking for a re-cut — reads the same answer from here.
 *
 * It has to be one place. Clips can now be cut in two places: on the server
 * from an uploaded recording, and on the streamer's own machine from the file
 * OBS left behind. Two implementations of "where does this clip start" would
 * disagree the first time either was touched, and the symptom would be a clip
 * that moves when you correct it — which reads as the correction being broken.
 *
 * Pure. The caller supplies the events and the sync numbers, and does whatever
 * it likes with the plan.
 */
import { audienceScore, peakPoints, suggestionWindows, voicePoints } from "./index.js";
import { deriveReactions, toRecordingTime } from "./index.js";
import { gameForReaction, gameSpans } from "./index.js";
import { z } from "zod";
/**
 * The clips for a recording, marked ones first and then the suggestions.
 *
 * Ordinals are assigned here rather than by the caller, because they are the
 * uniqueness key a stored clip is upserted on: two callers numbering
 * independently would collide the moment both ran for the same session.
 *
 * Two things are dropped rather than stored, and both happen when somebody
 * started recording after the session: a clip whose boundaries invert, and one
 * that lands entirely before the recording begins. A clip that merely straddles
 * the start is kept, clamped to zero, because part of that moment really is in
 * the file.
 *
 * The negative case is easy to miss -- a clip from -191s to -176s has a positive
 * length and passes an inversion check happily. ffmpeg is then asked to seek to
 * minus three minutes, which it answers with a message about nothing.
 */
/**
 * Boundaries that exist inside the recording, or null if none of it does.
 *
 * Clamped rather than refused when a clip straddles the start: a moment that
 * began before the recording did is still partly in the file, and half a clip
 * beats none.
 */
function inRecording(startMs, endMs) {
    if (endMs <= startMs)
        return null;
    // Entirely before the first frame. There is no footage to cut.
    if (endMs <= 0)
        return null;
    return { startMs: Math.max(0, startMs), endMs };
}
export function planClips(input) {
    const { events, mode, syncOffsetMs, driftRatio } = input;
    const derived = deriveReactions(events, mode);
    const peaks = peakPoints(events);
    // Deliberately kept out of audienceScore: that number is the crowd's verdict
    // and is averaged per game, so mixing the streamer's own shouting into it
    // would leave every per-game comparison meaning something else.
    const voice = voicePoints(events);
    const games = gameSpans(events);
    const suggestions = suggestionWindows(derived, peaks, voice);
    const clips = [];
    let ordinal = 0;
    for (const r of derived) {
        const bounds = inRecording(toRecordingTime(r.start_ms, syncOffsetMs, driftRatio), toRecordingTime(r.end_ms, syncOffsetMs, driftRatio));
        if (!bounds)
            continue;
        const { startMs, endMs } = bounds;
        // By the reaction's own clock and by its midpoint: clip boundaries carry
        // padding, and the first clip of a game can legitimately begin before the
        // game_start that caused it.
        const game = gameForReaction(games, r);
        ordinal += 1;
        clips.push({
            ordinal,
            startMs,
            endMs,
            segments: r.segments,
            videoId: r.video_id,
            audienceScore: audienceScore(r, peaks),
            suggested: false,
            suggestedBy: null,
            gameId: game?.id ?? null,
            gameName: game?.name ?? null,
        });
    }
    for (const s of suggestions) {
        const bounds = inRecording(toRecordingTime(s.start_ms, syncOffsetMs, driftRatio), toRecordingTime(s.end_ms, syncOffsetMs, driftRatio));
        if (!bounds)
            continue;
        const { startMs, endMs } = bounds;
        const game = gameForReaction(games, s);
        ordinal += 1;
        clips.push({
            ordinal,
            startMs,
            endMs,
            segments: 1,
            // A suggestion has no video: nobody was playing one, or nobody marked it.
            videoId: null,
            // The crowd's own count, and zero for a voice-only suggestion, which is
            // accurate: the crowd did not react there.
            audienceScore: s.hits,
            suggested: true,
            suggestedBy: s.source,
            gameId: game?.id ?? null,
            gameName: game?.name ?? null,
        });
    }
    return {
        clips,
        marked: clips.filter((c) => !c.suggested).length,
        suggested: clips.filter((c) => c.suggested).length,
        audiencePeaks: peaks.length,
        voicePeaks: voice.length,
    };
}
/** Seconds, for ffmpeg, which takes them rather than milliseconds. */
export function clipSeconds(clip) {
    return {
        start: clip.startMs / 1000,
        duration: (clip.endMs - clip.startMs) / 1000,
    };
}
/**
 * How a render differs from a plain landscape one, as a filename tag.
 *
 * Without this, cutting a stream twice — once landscape for YouTube, once
 * portrait for TikTok — writes both to the same name and the second silently
 * replaces the first. The streamer asks for both, watches two renders finish,
 * and ends up with one file.
 *
 * Empty for the default shape, so the plain case keeps the name it has always
 * had and nothing already on disk is orphaned. The watermark is deliberately
 * absent: it follows the plan rather than a choice, so it cannot be the
 * difference between two renders the same person asked for.
 */
export function clipVariant(spec) {
    const parts = [];
    if (spec.vertical)
        parts.push("portrait");
    if (spec.captions)
        parts.push("cc");
    if (spec.logo)
        parts.push("logo");
    return parts.join("-");
}
/**
 * A filename for a locally rendered clip.
 *
 * The ordinal leads so a folder sorts in stream order, and the game or video
 * is not in it: those come from the streamer's own files and can contain
 * anything, including characters a filesystem refuses.
 *
 * `variant` carries what `clipVariant` returned, so the same moment cut two
 * ways lands in two files rather than one overwriting the other.
 */
export function clipFileName(sessionId, ordinal, ext = "mp4", variant = "") {
    const n = String(ordinal).padStart(3, "0");
    const safe = sessionId.replace(/[^A-Za-z0-9_-]/g, "");
    const tag = variant ? `-${variant.replace(/[^A-Za-z0-9_-]/g, "")}` : "";
    return `${safe}-${n}${tag}.${ext}`;
}
// ---------------------------------------------------------------------------
// Registering a session whose clips are cut on the streamer's own machine
// ---------------------------------------------------------------------------
// Studio does not send the clip list. The server already has every event and
// runs planClips itself, so there is one computation rather than two that have
// to agree -- and nothing a client says can move a boundary except through the
// ordinary edit endpoint, which records that a human did it.
//
// What only the client can know is where the session sits inside the file and
// whether the recorder's clock drifted. Both are measured against the actual
// recording, which is on that machine, and measuring them there is faster than
// the server scanning a file it would have to fetch first.
export const LocalSessionInput = z.object({
    session_id: z.string().min(1).max(64),
    /** The recording's own name, for the dashboard. Never its path. */
    filename: z.string().min(1).max(500),
    duration_ms: z.number().int().positive().optional(),
    recorded_at: z.string().datetime().optional(),
    /**
     * Where the session's zero sits in the recording, as measured against the
     * file. Optional: the recorder usually reported it during the session and
     * the server kept that, which is used when this is absent.
     */
    sync_offset_ms: z.number().int().optional(),
    /**
     * Clock drift between the session and the recording.
     *
     * Bounded hard, because a ratio far from 1 means the wrong tone was matched
     * and applying it would be far worse than applying no correction at all --
     * the same bound the server-side detector uses.
     */
    drift_ratio: z.number().min(0.98).max(1.02).optional(),
    /**
     * The recording's pixel size, when the recorder reported it.
     *
     * Only used to work out the vertical crop. The server would otherwise
     * have to probe the file, and the file is the one thing it does not have
     * on this path. Absent means the crop falls back to centred, which is
     * what a failed probe does on the other path.
     */
    source_width: z.number().int().min(2).max(16384).optional(),
    source_height: z.number().int().min(2).max(16384).optional(),
});
export const LocalClipInput = z.object({
    reaction_id: z.string().min(1).max(64),
    size_bytes: z.number().int().positive(),
    start_ms: z.number().int().min(0),
    end_ms: z.number().int().positive(),
    duration_ms: z.number().int().positive().optional(),
    vertical: z.boolean().optional(),
    captions: z.boolean().optional(),
    logo: z.boolean().optional(),
});
//# sourceMappingURL=clip-plan.js.map